import ClassNames from "classnames";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import { style } from "typestyle";
import globalTranslations from "../../i18n/index.js";
import {
  BaseModuleProps,
  ColorScheme,
  QuestionAndAnswerModuleSettings,
  RichEditorState,
} from "../../types/index.js";
import { getTranslations, onTransitionEnd } from "../../utils/utils.js";
import Icon from "../Icon.js";
import RichEditorWrapper from "../RichEditorWrapper.js";

interface Props extends BaseModuleProps<QuestionAndAnswerModuleSettings> {
  scheme: ColorScheme;
  richEditor: RichEditorState;
}

const QuestionAndAnswerModule: FunctionComponent<Props> = ({
  translatedModule,
  translatedModule: {
    id: moduleId,
    translation: {
      languageId,
      settings: { title },
    },
    settings: { textAlign },
  },
  pageId,
  isPreview,
  scheme,
  isActive,
  richEditor,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [height, setHeight] = useState<number>();
  const isRichEditorOpen = !!richEditor[moduleId];
  const i18n = getTranslations(languageId, globalTranslations);
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (isActive || isRichEditorOpen) && setIsExpanded(true);
  }, [isActive, isRichEditorOpen]);

  return (
    <>
      <dt
        className={ClassNames(
          "QuestionsAndAnswersModule__Question",
          style({
            color: scheme.main.title,
            backgroundColor: scheme.main.background,
          }),
        )}
      >
        <button
          className="QuestionsAndAnswersModule__QuestionBtn Btn--bare"
          type="button"
          aria-expanded={isExpanded}
          aria-controls={moduleId}
          onClick={() => setIsExpanded((prevIsExpanded) => !prevIsExpanded)}
          aria-label={isExpanded ? i18n.less : i18n.more}
        >
          <span className="QuestionsAndAnswersModule__QuestionBtnInner">
            <span
              className={`QuestionsAndAnswersModule__QuestionText TextAlign--${textAlign.title}`}
            >
              {title}
            </span>
            <Icon
              className="QuestionsAndAnswersModule__Icon"
              glyph={isExpanded ? "arrow-up" : "arrow-down"}
            />
          </span>
        </button>
      </dt>
      <CSSTransition
        in={isExpanded}
        timeout={1000}
        classNames={{
          enter: "QuestionsAndAnswersModule__Answer--entering",
          enterDone: "QuestionsAndAnswersModule__Answer--entered",
          exitActive: "QuestionsAndAnswersModule__Answer--exiting",
          exitDone: "QuestionsAndAnswersModule__Answer--exited",
        }}
        addEndListener={(done) => {
          nodeRef.current && onTransitionEnd(nodeRef.current, done);
        }}
        nodeRef={nodeRef}
        className={ClassNames(
          "QuestionsAndAnswersModule__Answer",
          style({
            borderColor: scheme.main.title,
            backgroundColor: scheme.main.background,
          }),
        )}
        onEnter={() => setHeight(nodeRef.current?.scrollHeight)}
        onEntered={() => setHeight(undefined)}
        onExit={() => setHeight(nodeRef.current?.scrollHeight)}
        onExiting={() => setHeight(undefined)}
      >
        <dd id={moduleId} style={{ height }} ref={nodeRef}>
          <RichEditorWrapper
            className={`QuestionsAndAnswersModule__AnswerText TextAlign--${textAlign.description}`}
            module={translatedModule}
            pageId={pageId}
            languageId={languageId}
            isPreview={isPreview}
            scheme={scheme}
          />
        </dd>
      </CSSTransition>
    </>
  );
};

export default QuestionAndAnswerModule;
